import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import * as CapLedgerService from '@/services/capLedgerService';
import { planShareBalanceChange, type ReductionReason } from '@/utils/shareBalanceChange';
import { ReductionReasonField } from './ReductionReasonField';

interface ShareAdjustmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  shareholderId: string;
  shareholderName: string;
  companyId: string;
  currentShares: number;
}

export const ShareAdjustmentDialog = ({
  open,
  onOpenChange,
  onSuccess,
  shareholderId,
  shareholderName,
  companyId,
  currentShares,
}: ShareAdjustmentDialogProps) => {
  const [newShares, setNewShares] = useState(currentShares.toString());
  const [loading, setLoading] = useState(false);
  const [reductionReason, setReductionReason] = useState<ReductionReason | undefined>();

  // Planned without a reason on purpose: a 'needs-reason' answer is how the form
  // learns the holding is going down and that the question has to be asked.
  const reductionPreview = planShareBalanceChange(currentShares, newShares);
  const isReducing = reductionPreview.kind === 'needs-reason';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const plan = planShareBalanceChange(currentShares, newShares, reductionReason);

    if (plan.kind === 'invalid') {
      toast.error(plan.error);
      return;
    }

    if (plan.kind === 'unchanged') {
      toast.error('No change in share count');
      return;
    }

    if (plan.kind === 'needs-reason') {
      toast.error('Choose whether the shares are cancelled or the balance is being corrected');
      return;
    }

    setLoading(true);

    try {
      // A signed event, so summing the column stays correct. The planner picks
      // ISSUANCE for a holder starting from nothing and ADJUSTMENT thereafter.
      await CapLedgerService.insertLedgerEvent({
        companyId,
        shareholderId,
        eventType: plan.eventType,
        shares: plan.shares,
        shareClass: 'Ordinary',
        noteReference: plan.noteReference,
      });

      // Recalculate cap table to update summary
      await CapLedgerService.recalcCapTable(companyId);

      toast.success(`Share count adjusted for ${shareholderName}`);
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error adjusting shares:', error);

      let errorMessage = 'Failed to adjust shares';
      if (error?.message) {
        errorMessage += `: ${error.message}`;
      }
      if (error?.hint) {
        errorMessage += ` (${error.hint})`;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!loading) {
      onOpenChange(open);
      if (!open) {
        setNewShares(currentShares.toString());
        setReductionReason(undefined);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Same reason as ShareholderDialog: the reduction question makes this
          form tall enough to push its own footer past the viewport. */}
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Adjust Share Count</DialogTitle>
            <DialogDescription>Adjust the number of shares for {shareholderName}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Shares</Label>
              <Input value={currentShares.toLocaleString()} disabled className="bg-muted" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newShares">New Share Count</Label>
              <Input
                id="newShares"
                type="number"
                value={newShares}
                onChange={(e) => setNewShares(e.target.value)}
                placeholder="Enter new share count"
                min="0"
                required
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>

            {reductionPreview.kind === 'change' && (
              <div className="rounded-lg bg-muted p-3 text-sm">
                <p className="font-medium">
                  Adjustment: {reductionPreview.shares > 0 ? '+' : ''}
                  {reductionPreview.shares.toLocaleString()} shares
                </p>
              </div>
            )}

            {reductionPreview.kind === 'needs-reason' && (
              <ReductionReasonField
                reducingBy={reductionPreview.reducingBy}
                value={reductionReason}
                onChange={setReductionReason}
              />
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            {/* Blocked rather than warned about: the answer decides what the
                register says happened, so there is nothing sensible to submit
                until it is given. */}
            <Button type="submit" disabled={loading || (isReducing && !reductionReason)}>
              {loading ? 'Adjusting...' : 'Adjust Shares'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
